import { skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { inferInput, inferOutput } from '@trpc/tanstack-react-query';
import { useTRPC } from './trpc.js';

export const STATUS_POLL_MS = 15_000;

type Trpc = ReturnType<typeof useTRPC>;
// Derived from the router so the server's AD_NETWORK_PROVIDERS stays the only list.
export type AdProvider = inferInput<Trpc['oauth']['getStatus']>['provider'];
export type AdNetworkStatus = inferOutput<Trpc['oauth']['getStatus']>;
export type ConnectionState = AdNetworkStatus['state'];
export type SubmissionResult = inferOutput<Trpc['oauth']['submitAccounts']>['submissions'][number];
export type ProductSummary = inferOutput<Trpc['products']['list']>[number];

// Status polling and mutations opt out of stream batching: they are independent
// requests and plain HTTP responses keep them mockable in e2e.
const NO_STREAM = { context: { stream: false } };

// Products in scope whose client gets Integrations; the server decides and enforces it.
export function useIntegrationProducts() {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.products.list.queryOptions(),
    select: (products) => products.filter((p) => p.integrationsEnabled),
  });
}

export function useAdNetworkStatus(provider: AdProvider, productId: number | undefined) {
  const trpc = useTRPC();
  return useQuery(
    trpc.oauth.getStatus.queryOptions(
      productId === undefined ? skipToken : { provider, productId },
      {
        refetchInterval: (query) =>
          query.state.data?.state === 'pending' ? STATUS_POLL_MS : false,
        trpc: NO_STREAM,
      },
    ),
  );
}

export function useCandidates(
  provider: AdProvider,
  productId: number | undefined,
  enabled: boolean,
) {
  const trpc = useTRPC();
  return useQuery(
    trpc.oauth.listCandidates.queryOptions(
      enabled && productId !== undefined ? { provider, productId } : skipToken,
      { retry: false },
    ),
  );
}

export function useSubmitAccounts() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  return useMutation(
    trpc.oauth.submitAccounts.mutationOptions({
      trpc: NO_STREAM,
      onSuccess: async (_data, vars) => {
        const scope = { provider: vars.provider, productId: vars.productId };
        await Promise.all([
          queryClient.invalidateQueries(trpc.oauth.getStatus.queryFilter(scope)),
          queryClient.invalidateQueries(trpc.oauth.listCandidates.queryFilter(scope)),
        ]);
      },
    }),
  );
}
